import type { WeakValidationMap } from 'react';
import type React from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { describe, PropTypes } from '../props-helpers/index.js';

const propTypes = {
  onFirstFocusCb: PropTypes.func
    .description('The function to be invoked when focus enters the wrapping container for the first time')
    .signature('((e: React.FocusEvent, ...args: unknown[]) => void)').isRequired,
  onBlurCb: PropTypes.func
    .description('Optional onBlur function to be invoked along the returned onBlur handler')
    .signature('((e: React.FocusEvent, ...args: unknown[]) => void)'),
} as WeakValidationMap<unknown>;

type OnBlurCb = (e: React.FocusEvent, ...args: unknown[]) => void;
type OnFirstFocusCb = (e: React.FocusEvent, ...args: unknown[]) => void;
type UseFirstFocusIn = ({ onFirstFocus, onBlur }: { onFirstFocus: OnFirstFocusCb; onBlur: OnBlurCb }) => {
  onFirstFocus: OnFirstFocusCb;
  onBlur: OnBlurCb;
};

const useOnFirstFocusIn: UseFirstFocusIn = ({ onFirstFocus, onBlur }) => {
  const mutableDidChildBlur = useRef(false);
  const onFirstFocusHelper = useCallback<OnFirstFocusCb>(
    (e, ...args) => {
      const { currentTarget } = e;
      // Give browser time to focus the next element
      requestAnimationFrame(() => {
        const isChildFocus = currentTarget.contains(document.activeElement);
        const isFirstFocus = isChildFocus && !mutableDidChildBlur.current;
        if (isFirstFocus) {
          onFirstFocus(e, ...args);
        }
      });
    },
    [onFirstFocus],
  );
  const onBlurHelper = useCallback<OnBlurCb>(
    (e, ...args) => {
      onBlur?.(e, ...args);
      const { currentTarget } = e;
      // Give browser time to focus the next element
      requestAnimationFrame(() => {
        const isChildBlur = currentTarget.contains(document.activeElement);
        mutableDidChildBlur.current = isChildBlur;
      });
    },
    [onBlur],
  );
  return useMemo(
    () => ({
      onFirstFocus: onFirstFocusHelper,
      onBlur: onBlurHelper,
    }),
    [onFirstFocusHelper, onBlurHelper],
  );
};

const UseOnFirstFocusInWithSchema = describe(useOnFirstFocusIn);
UseOnFirstFocusInWithSchema.propTypes = propTypes;

export { useOnFirstFocusIn, UseOnFirstFocusInWithSchema };
